import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import {
  deletePostWithSubcollections,
  postHasModeratedChildren,
} from "../utils/deletePostWithSubcollections";
import { useNavigate } from "react-router-dom";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db, auth, storage } from "../firebaseConfig";
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import "./MyActivityPage.css";
import "./PostCard.css"; // reuse image/link preview styles
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen } from "@fortawesome/free-solid-svg-icons";
import { saveToGallery, formatSaveMessage } from "../utils/saveToGallery";
import { isPostExpired } from "../utils/postLifecycle";
import ZoomableImage from "../components/ZoomableImage";

const MyActivityPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState({});
  const [editingPost, setEditingPost] = useState(null);
  const [editText, setEditText] = useState("");
  const [editTopic, setEditTopic] = useState("");
  const [editLinkUrl, setEditLinkUrl] = useState("");
  const [editImages, setEditImages] = useState([]); // existing images kept
  const [newFiles, setNewFiles] = useState([]); // newly attached files
  const [showEditModal, setShowEditModal] = useState(false);
  const [previewImg, setPreviewImg] = useState(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [deletingPostId, setDeletingPostId] = useState("");
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const getActorName = (post, user) =>
    post.userName || user?.displayName || user?.email || "Post author";

  useEffect(() => {
    const timerId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 60 * 1000);

    return () => window.clearInterval(timerId);
  }, []);

  // ✅ Listen for user's posts
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const q = query(
      collection(db, "posts"),
      where("userId", "==", user.uid),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() })).filter((p) => !p.isHidden);
        setPosts(list);
        setLoading(false);
      },
      (err) => {
        console.error("Error loading MyActivity:", err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const visiblePosts = useMemo(
    () => posts.filter((post) => !isPostExpired(post, nowMs)),
    [posts, nowMs]
  );

  // ✅ Move post to trashBin
  const deletePost = async (post) => {
    if (deletingPostId) return;
    if (!window.confirm("Delete this post?")) return;
    const user = auth.currentUser;
    if (!user) return;

    try {
      setDeletingPostId(post.id);
      if (await postHasModeratedChildren(post.id)) {
        await deletePostWithSubcollections(post.id, {
          preserveIfModerated: true,
          actorUid: user.uid,
          actorName: getActorName(post, user),
        });
        alert("Post removed from your activity and retained for moderation review.");
        return;
      }

      const trashRef = doc(db, "trashBin", post.id);
      await setDoc(trashRef, {
        ...post,
        deletedBy: user.uid,
        deletedAt: serverTimestamp(),
        expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        isRestorable: true,
        commentCount: 0,
        imagePaths: Array.isArray(post.imagePaths) ? post.imagePaths : [],

      });
      await deletePostWithSubcollections(post.id);
      alert("Post deleted.");
    } catch (e) {
      console.error("Failed to delete post", e);
      alert("Could not delete post. Please try again or contact an admin.");
    } finally {
      setDeletingPostId("");
    }
  };

  // Open edit modal
  const openEdit = (post) => {
    setEditingPost(post);
    setEditText(post.text || "");
    setEditTopic(post.topic || "");
    setEditLinkUrl(post.linkUrl || "");
    setEditImages(Array.isArray(post.images) ? post.images.slice() : []);
    setNewFiles([]);
    setShowEditModal(true);
  };

  // Save edits to Firestore
  const saveEdit = async () => {
    if (!editingPost || isSavingEdit) return;
    try {
      setIsSavingEdit(true);
      // Safeguard: prevent empty text
      if (!editText.trim()) {
        alert("Post text is required.");
        return;
      }

      // Upload any newly added files
      const user = auth.currentUser;
      const uploadedUrls = [];
      const uploadedPaths = [];
      if (newFiles && newFiles.length > 0 && user) {
        for (const file of newFiles) {
          const path = `posts/${user.uid}/${Date.now()}_${file.name}`;
          const r = storageRef(storage, path);
          await uploadBytes(r, file);
          const url = await getDownloadURL(r);
          uploadedUrls.push(url);
          uploadedPaths.push(path);
        }
      }

      // Determine removed images (present in original but not in current editImages)
      const originalImages = Array.isArray(editingPost.images) ? editingPost.images : [];
      const originalImagePaths = Array.isArray(editingPost.imagePaths) ? editingPost.imagePaths : [];
      const finalImages = [...editImages, ...uploadedUrls].slice(0, 3);
      const keptPaths = editImages.map((url) => {
        const index = originalImages.indexOf(url);
        return index >= 0 ? originalImagePaths[index] : "";
      }).filter(Boolean);
      const finalImagePaths = [...keptPaths, ...uploadedPaths].slice(0, 3);
      const removed = originalImages.filter((u) => !finalImages.includes(u));
      const removedPaths = originalImages
        .map((url, index) => finalImages.includes(url) ? "" : originalImagePaths[index])
        .filter(Boolean);

      // Best-effort delete removed files from storage
      for (const url of removed) {
        try {
          const r = storageRef(storage, url);
          await deleteObject(r);
        } catch (e) {
          // ignore failures (may be non-storage URL)
        }
      }
      for (const path of removedPaths) {
        try {
          const r = storageRef(storage, path);
          await deleteObject(r);
        } catch (e) {
          // ignore failures; URL fallback above may already have deleted it
        }
      }

      await updateDoc(doc(db, "posts", editingPost.id), {
        text: editText,
        topic: editTopic,
        linkUrl: editLinkUrl.trim() ? editLinkUrl.trim() : null,
        images: finalImages,
        imagePaths: finalImagePaths,
        updatedAt: serverTimestamp(),
      });
      setShowEditModal(false);
      setEditingPost(null);
    } catch (e) {
      console.error("Failed to save edit", e);
      alert("Could not save changes. Please try again.");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const closeEdit = () => {
    setShowEditModal(false);
    setEditingPost(null);
  };

  if (loading)
    return (
      <div className="myactivity-page">
        <h3>Loading your activity…</h3>
      </div>
    );

    

  return (
    <div className="myactivity-page">
    {/* ✅ Fixed back button outside the scroll area */}
<div className="activity-header">
  <h1>📊 My Activity</h1>
</div>
<button
  className="wire-fixed"
  onClick={() => navigate("/thewire")}
>
  📰 ← The Wire
</button>

      {visiblePosts.length === 0 ? (
        <p>You haven’t posted anything yet.</p>
      ) : (
        <>
          <div className="activity-summary">
            <strong>Posts:</strong> {visiblePosts.length} | 
            <strong>Total Likes:</strong>{" "}
            {visiblePosts.reduce((a, p) => a + (p.likes || 0), 0)} | 
            <strong>Views:</strong>{" "}
            {visiblePosts.reduce((a, p) => a + (p.views || 0), 0)} | 
            <strong>Comments:</strong>{" "}
            {visiblePosts.reduce((a, p) => a + (p.commentCount || 0), 0)}
          </div>

          {visiblePosts.map((p) => (
            <div key={p.id} className={`activity-card ${expandedIds[p.id] ? "expanded" : ""}`}>
              <div className="activity-header-row">
                <h3>{p.topic || "Untitled Post"}</h3>
              </div>
              <p className="activity-text">
                {p.text?.slice(0, 180) || "(No text content)"} …
              </p>
              <p className="activity-text-full">{p.text || "(No text content)"}</p>
              {p.text && p.text.length > 150 && (
                <button
                  className="view-more-btn"
                  onClick={() =>
                    setExpandedIds((prev) => ({ ...prev, [p.id]: !prev[p.id] }))
                  }
                >
                  {expandedIds[p.id] ? "Show less" : "Show more"}
                </button>
              )}
              {Array.isArray(p.images) && p.images.length > 0 && (
                <div className="post-images">
                  {p.images.map((img, idx) => (
                    <img
                      key={idx}
                      src={img}
                      alt="Post attachment"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        document.body.classList.add("modal-open");
                        setPreviewImg(img);
                      }}
                    />
                  ))}
                </div>
              )}
              {p.linkUrl && <LinkPreview url={p.linkUrl} />}
              <div className="activity-stats">
                👍 {p.likes || 0} 💬 {p.commentCount || 0} 🕶️ {p.views || 0}
              </div>
              <div className="activity-timestamp">{p.createdAt?.toDate ? p.createdAt.toDate().toLocaleString() : ""}</div>
              <div className="activity-actions">
                <button
                  onClick={() => navigate(`/thewire#post-${p.id}`)}
                  className="view-btn"
                >
                  View Post
                </button>
                <button
                  onClick={() => openEdit(p)}
                  className="edit-btn"
                  aria-label="Edit post"
                  title="Edit post"
                >
                  <FontAwesomeIcon icon={faPen} style={{ marginRight: 6 }} />
                  Edit
                </button>
                <button
                  onClick={() => deletePost(p)}
                  className="delete-btn"
                  disabled={Boolean(deletingPostId)}
                >
                  {deletingPostId === p.id ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          ))}
        </>
      )}
      {showEditModal && (
        <div className="edit-modal-overlay" onClick={closeEdit}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Post</h3>
            <input
              type="text"
              placeholder="Topic (optional)"
              value={editTopic}
              onChange={(e) => setEditTopic(e.target.value)}
              className="edit-input"
            />
            <textarea
              rows="6"
              placeholder="Update your post text"
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              className="edit-textarea"
            />
            {/* Link editor */}
            <input
              type="url"
              placeholder="Video or article link (optional)"
              value={editLinkUrl}
              onChange={(e) => setEditLinkUrl(e.target.value)}
              className="edit-input"
              style={{ marginTop: 8 }}
            />
            {editLinkUrl && (
              <button
                className="cancel-btn"
                onClick={() => setEditLinkUrl("")}
                style={{ marginTop: 6 }}
              >
                Remove Link
              </button>
            )}

            {/* Existing images with remove */}
            {editImages && editImages.length > 0 && (
              <div className="edit-previews">
                {editImages.map((img, idx) => (
                  <div key={idx} className="thumb">
                    <img src={img} alt="Post attachment" />
                    <button
                      type="button"
                      className="remove"
                      onClick={() => setEditImages((prev) => prev.filter((u) => u !== img))}
                      aria-label="Remove image"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add new attachments */}
            <label className="image-upload" style={{ display: "inline-block", marginTop: 10 }}>
              Add Image(s)
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  setNewFiles((prev) => {
                    const remaining = Math.max(0, 3 - (editImages.length + prev.length));
                    if (remaining <= 0) {
                      alert('You can attach up to 3 images total.');
                      return prev;
                    }
                    const toAdd = files.slice(0, remaining);
                    return [...prev, ...toAdd];
                  });
                }}
                disabled={(editImages.length + newFiles.length) >= 3}
              />
            </label>
            {newFiles && newFiles.length > 0 && (
              <div className="edit-previews">
                {newFiles.map((f, idx) => {
                  const src = URL.createObjectURL(f);
                  return (
                    <div key={idx} className="thumb">
                      <img src={src} alt={`New ${idx + 1}`} onLoad={() => URL.revokeObjectURL(src)} />
                      <button
                        type="button"
                        className="remove"
                        onClick={() => setNewFiles((prev) => prev.filter((_, i) => i !== idx))}
                        aria-label="Remove new image"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="edit-actions">
              <button
                className="save-btn"
                onClick={saveEdit}
                disabled={isSavingEdit || !editText.trim()}
                title={!editText.trim() ? "Post text required" : "Save changes"}
              >
                {isSavingEdit ? "Saving..." : "Save"}
              </button>
              <button className="cancel-btn" onClick={closeEdit} disabled={isSavingEdit}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {previewImg &&
        ReactDOM.createPortal(
          <div
            className="image-preview-overlay"
            onMouseDown={(e) => {
              if (e.target.classList.contains("image-preview-overlay")) {
                setPreviewImg(null);
                document.body.classList.remove("modal-open");
              }
            }}
          >
            <div className="image-preview-modal">
              <ZoomableImage src={previewImg} alt="Full view" />
              <div className="image-preview-actions">
                <button
                  className="download-btn"
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      const result = await saveToGallery(previewImg);
                      alert(formatSaveMessage(result));
                    } catch (err) {
                      console.error("Failed to save image:", err);
                      alert("Could not save image.");
                    }
                  }}
                >
                  Download
                </button>
              </div>
              <button
                className="close-btn"
                onClick={() => {
                  setPreviewImg(null);
                  document.body.classList.remove("modal-open");
                }}
              >
               ❌
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default MyActivityPage;

// Thumbnail/embedded link previews (mirrors The Wire styles)
const LinkPreview = ({ url }) => {
  const lower = (url || "").toLowerCase();

  // Vimeo embed
  if (lower.includes("vimeo.com")) {
    const idMatch = lower.match(/vimeo\.com\/(\d+)/);
    const videoId = idMatch ? idMatch[1] : null;
    if (videoId) {
      return (
        <div className="embed-wrapper">
          <iframe
            src={`https://player.vimeo.com/video/${videoId}`}
            title="Vimeo video preview"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      );
    }
  }

  // Direct video file
  if (lower.endsWith(".mp4") || lower.endsWith(".mov") || lower.endsWith(".webm")) {
    return (
      <video className="embed-video" controls preload="metadata">
        <source src={url} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    );
  }

  // YouTube thumbnail preview
  if (lower.includes("youtube.com") || lower.includes("youtu.be")) {
    let videoId = null;
    const vParam = lower.match(/[?&]v=([^&]+)/);
    if (vParam) videoId = vParam[1];
    if (!videoId) {
      const short = lower.match(/youtu\.be\/([^?]+)/);
      if (short) videoId = short[1];
    }
    if (videoId) {
      const thumbUrl = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="video-preview-card"
        >
          <div className="video-thumb">
            <img
              src={thumbUrl}
              alt="YouTube thumbnail"
              onError={(e) => {
                e.target.src =
                  "https://www.gstatic.com/youtube/img/promos/growth/yt_logo_dark.png";
                e.target.style.objectFit = "contain";
                e.target.style.background = "#000";
              }}
            />
            <div className="video-overlay">Watch on YouTube</div>
          </div>
        </a>
      );
    }
  }

  // Fallback: show as a styled link
  return (
    <a className="post-link" href={url} target="_blank" rel="noopener noreferrer">
      {url}
    </a>
  );
};



